<!--
  PLACEHOLDER — Income Estimate (demo)

  Full-page admin tool rendered at the DashboardView layer (replaces
  BigProducerDashboard when ?view=income-estimate is set AND the company
  has the `income_estimate_enabled` feature flag on). Visibility is
  per-company; toggle via the admin Company Feature Flags panel.

  All numbers, streams, and pricing on this page come from the static
  dataset in incomeEstimateDemoData.ts. The only thing wired to the live
  company is the eyebrow (name + registry code). When this becomes a real
  feature:
    - Replace incomeEstimateDemoData.ts with a real composable/API.
    - Hook the HorizonToolbar selection through to the data fetcher.
    - Wire SpotConfigModal Apply to actually recompute spot stream values
      (currently just closes; see modal file for details).
    - Decide whether to drop the feature flag or keep it as opt-in.

  Spec: finance-planner-ronny-lockin/project/HANDOFF.md

  Styling note: layout, typography, padding, and color all come from
  Bootstrap utility classes + Phoenix utilities (`font-serif`,
  `eyebrow-label`, `text-body-secondary`, `border-dashed`, `fs-N`, `card`,
  `card-body`, etc.). The only scoped CSS is the colored swatches in the
  chart legend + footnotes, since Bootstrap has no swatch primitive and
  the 45° hatched fill is design-specific.
-->
<template>
  <div class="income-page-root text-start mx-auto py-2">
    <button
      type="button"
      class="btn btn-link text-decoration-none text-body-secondary p-0 mb-3 d-inline-flex align-items-center fw-medium"
      @click="goBackToDashboard"
    >
      <font-awesome-icon icon="arrow-left" class="me-2" />
      Back to dashboard
    </button>

    <div class="mb-3">
      <div class="eyebrow-label mb-1">{{ eyebrow }}</div>
      <h1 class="font-serif mb-1">Income estimate</h1>
      <p class="income-page-sub text-body-secondary mb-0 fs-sm-9">
        Net of Soldera fees. PPAs, forward sales, and spot sales are computed
        from your portfolio configuration and the country-aware production
        projection. Locked numbers come from contracts; estimates use
        weighted-average market prices and projected production curves.
      </p>
    </div>

    <div class="d-flex flex-column gap-3">
      <div class="card overflow-hidden">
        <IncomeKpiStrip :horizon="horizon" :streams="streams" />
        <HorizonToolbar
          v-model:horizon="horizon"
          @configure-spot="openSpotConfig"
        />
      </div>

      <div class="card">
        <div class="card-body">
          <div
            class="d-flex justify-content-between align-items-end gap-3 mb-3"
          >
            <div>
              <div class="eyebrow-label mb-1">
                PROJECTED INCOME · NET OF FEES
              </div>
              <h3 class="font-serif fs-7 mb-0">
                Monthly net income · stacked by category
              </h3>
            </div>
            <div class="chart-legend d-flex gap-3 small">
              <span class="legend-row legend-row--ppa">
                <span class="sw" />
                PPA
              </span>
              <span class="legend-row text-primary">
                <span class="sw legend-sw-primary" />
                Forward
              </span>
              <span class="legend-row text-warning">
                <span class="sw legend-sw-warning" />
                Spot
              </span>
              <span class="legend-row text-body-secondary">
                <span class="sw hatched" />
                Estimated portion
              </span>
            </div>
          </div>
          <IncomeChart
            :horizon="horizon"
            :streams="streams"
            v-model:hover="hover"
          />
          <div
            class="footnotes border-top border-dashed mt-2 pt-2 small text-body-secondary d-flex flex-column gap-1"
          >
            <div>
              <span class="key">
                <span class="key-sw fixed" />
                Locked — fixed-price contract, fixed delivery
              </span>
              <span class="key">
                <span class="key-sw variable" />
                Estimated — pay-as-produced or projected volumes
              </span>
              <span class="key">
                <span class="key-sw tentative" />
                Spot — projected from last-N weighted-average price
              </span>
            </div>
            <div class="footnotes-meta text-body-tertiary small">
              Estimates use the country-aware production projection service.
              Spot price method is configurable. Soldera fee of {{ feePct }}%
              deducted.
            </div>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-body">
          <div
            class="d-flex justify-content-between align-items-end gap-3 mb-3"
          >
            <div>
              <div class="eyebrow-label mb-1">BREAKDOWN BY STREAM</div>
              <h3 class="font-serif fs-7 mb-0">
                Per-stream monthly net income · {{ horizonSubheading }}
              </h3>
            </div>
          </div>
          <AlignedTable
            :horizon="horizon"
            :streams="streams"
            v-model:hover="hover"
          />
        </div>
      </div>
    </div>

    <SpotConfigModal ref="spotConfigModal" v-model:prices="spotPrices" />
  </div>
</template>

<script setup lang="ts">
import { useQueryManager } from "@/composables/useQueryManager";
import { computed, ref, useTemplateRef } from "vue";
import AlignedTable from "./AlignedTable.vue";
import HorizonToolbar from "./HorizonToolbar.vue";
import IncomeChart from "./IncomeChart.vue";
import IncomeKpiStrip from "./IncomeKpiStrip.vue";
import SpotConfigModal from "./SpotConfigModal.vue";
import {
  buildDemoStreams,
  DEFAULT_SPOT_PRICES,
  SOLDERA_FEE_PCT,
  type HorizonValue,
  type SpotPrices,
} from "./incomeEstimateDemoData";

const props = defineProps<{
  companyName: string;
  registryCode: string | null;
}>();

const { updateQuery } = useQueryManager();

const horizon = ref<HorizonValue>("next-12");
const spotConfigModal =
  useTemplateRef<InstanceType<typeof SpotConfigModal>>("spotConfigModal");
// Deep clone so per-instance edits (e.g. SpotConfigModal's override field)
// can't mutate the module-level DEFAULT_SPOT_PRICES.renewable object.
const spotPrices = ref<SpotPrices>({
  ...DEFAULT_SPOT_PRICES,
  renewable: { ...DEFAULT_SPOT_PRICES.renewable },
});
const hover = ref<number | null>(null);

const openSpotConfig = () => spotConfigModal.value?.show();

const streams = buildDemoStreams();
const feePct = Math.round(SOLDERA_FEE_PCT * 100);

const eyebrow = computed(() => {
  const name = props.companyName.toUpperCase();
  return props.registryCode ? `${name} · ${props.registryCode}` : name;
});

const horizonSubheading = computed(() => {
  if (horizon.value === "two-year") return "next 2 years";
  if (horizon.value === "next-12") return "next 12 months";
  return "rest of 2026";
});

const goBackToDashboard = () => {
  updateQuery({ view: undefined });
};
</script>

<style scoped lang="scss">
/* Swatches (chart legend, footnotes) and two one-off content widths.
   Everything else (layout, typography, padding, colors) is handled by
   Bootstrap + Phoenix utility classes on the template. */

/* Page container max width — design wants 1240px which is between
   Bootstrap's container-xl (1140) and container-xxl (1320). */
.income-page-root {
  max-width: 1240px;
}

/* Sub-paragraph beneath the H1 has a custom 720px reading-width cap to
   keep the intro from running edge to edge on wide displays. */
.income-page-sub {
  max-width: 720px;
}

.legend-row {
  display: inline-flex;
  align-items: center;
  gap: 6px;

  .sw {
    width: 14px;
    height: 8px;
    border-radius: 1px;
    display: inline-block;

    &.hatched {
      background-image: repeating-linear-gradient(
        -45deg,
        currentColor,
        currentColor 3px,
        rgba(255, 255, 255, 0.55) 3px,
        rgba(255, 255, 255, 0.55) 5px
      );
    }
  }
}
.legend-row--ppa {
  color: #0d2218; // forest-darkest, not in Phoenix's ladder
  .sw {
    background: #0d2218;
  }
}
.legend-sw-primary {
  background: var(--phoenix-primary);
}
.legend-sw-warning {
  background: var(--phoenix-warning);
}

.footnotes .key {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-right: 14px;
}
.footnotes .key-sw {
  width: 12px;
  height: 8px;
  border-radius: 1px;
  display: inline-block;

  &.fixed {
    background: var(--phoenix-primary);
  }
  &.variable {
    background-image: repeating-linear-gradient(
      -45deg,
      var(--phoenix-warning),
      var(--phoenix-warning) 3px,
      rgba(255, 255, 255, 0.55) 3px,
      rgba(255, 255, 255, 0.55) 5px
    );
  }
  &.tentative {
    background: transparent;
    border: 1px dashed var(--phoenix-warning);
    height: 10px;
  }
}
</style>
